import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReactFlow } from 'reactflow';
import { MousePointer2, SkipForward, Sparkles, ArrowUp } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { useRevealStore } from '../../store/revealStore';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * WorkflowRevealOverlay — le enseña al usuario cómo funciona Studio Media Labs
 * mostrando cómo "nace" el workflow que Lumière (o una plantilla) ya creó.
 *
 * MUY IMPORTANTE: acá no se crea, configura ni persiste nada. Cuando este
 * componente arranca, el workflow ya está completo y válido en el store; lo
 * único que hace es ir sacando nodos/edges de la lista de "ocultos" del
 * `revealStore` mientras dibuja un clon visual que viaja desde la herramienta
 * del panel izquierdo hasta la posición final del nodo.
 *
 * Si algo falla, si el usuario toca el canvas o aprieta "Saltar animación",
 * se revela todo de una y el flujo queda utilizable.
 */

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const NODE_W = 320;
const NODE_H = 220;

/** Píxeles que el dock de LMX tapa por abajo (lo publica LmxDock en :root). */
const dockLift = () => parseFloat(getComputedStyle(document.documentElement)
  .getPropertyValue('--lmx-dock-lift')) || 0;

const boundsOf = (nodes) => {
  const xs = nodes.map((n) => n.position?.x ?? 0);
  const ys = nodes.map((n) => n.position?.y ?? 0);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const x2 = Math.max(...nodes.map((n) => (n.position?.x ?? 0) + (n.width || NODE_W)));
  const y2 = Math.max(...nodes.map((n) => (n.position?.y ?? 0) + (n.height || NODE_H)));
  return { x, y, width: Math.max(x2 - x, 1), height: Math.max(y2 - y, 1) };
};

const WorkflowRevealOverlay = () => {
  const active = useRevealStore((s) => s.active);
  const spotlightRun = useRevealStore((s) => s.spotlightRun);
  const revealNode = useRevealStore((s) => s.revealNode);
  const revealEdge = useRevealStore((s) => s.revealEdge);
  const finish = useRevealStore((s) => s.finish);
  const clearSpotlight = useRevealStore((s) => s.clearSpotlight);

  const reduced = useReducedMotion();
  const { flowToScreenPosition, fitBounds, setCenter } = useReactFlow();

  const [ghost, setGhost] = useState(null);
  const [halo, setHalo] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [runArrow, setRunArrow] = useState(null);

  const cancelled = useRef(false);
  const runningRef = useRef(false);

  const skip = useCallback(() => {
    cancelled.current = true;
    setGhost(null);
    setHalo(null);
    setCursor(null);
    finish();
  }, [finish]);

  // ── Secuencia didáctica ───────────────────────────────
  useEffect(() => {
    if (!active || runningRef.current) return undefined;
    runningRef.current = true;
    cancelled.current = false;

    const { order, frame } = useRevealStore.getState();
    const nodes = useWorkflowStore.getState().nodes;
    const total = order.length;
    // En pantallas chicas no hay espacio para el vuelo del ghost: los nodos
    // aparecen directo (más rápido y sin ruido visual).
    const compact = typeof window !== 'undefined' && window.innerWidth < 860;
    const simple = reduced || compact;
    // Didáctica pero rápida: los flujos grandes se aceleran.
    const f = total <= 4 ? 1 : total <= 8 ? 0.65 : 0.4;

    const revealReadyEdges = () => {
      const { hiddenNodes, hiddenEdges } = useRevealStore.getState();
      const edges = useWorkflowStore.getState().edges;
      const pending = hiddenEdges.filter((id) => {
        const edge = edges.find((e) => e.id === id);
        if (!edge) return false;
        return !hiddenNodes.includes(edge.source) && !hiddenNodes.includes(edge.target);
      });
      pending.forEach(revealEdge);
      return pending.length;
    };

    const run = async () => {
      try {
        // Encuadre final calculado UNA vez: así los destinos no se mueven.
        const targets = nodes.filter((n) => order.includes(n.id));
        if (targets.length) {
          try {
            // 'targets' (LMX): encuadra los nodos nuevos + sus vecinos, nunca
            // el proyecto entero; 'all' (plantillas, legacy) queda igual.
            let frameNodes = nodes;
            if (frame === 'targets') {
              const edges = useWorkflowStore.getState().edges;
              const ids = new Set(order);
              edges.forEach((e) => {
                if (ids.has(e.source)) ids.add(e.target);
                if (ids.has(e.target)) ids.add(e.source);
              });
              frameNodes = nodes.filter((n) => ids.has(n.id));
            }
            const b = boundsOf(frameNodes.length ? frameNodes : targets);
            // nunca acercar de más: mínimo 1100×600 de encuadre
            const w = Math.max(b.width, 1100); const h = Math.max(b.height, 600);
            if (frame === 'targets') {
              // El dock de LMX tapa la parte de abajo: el encuadre se calcula
              // sobre el área que queda visible.
              const lift = dockLift();
              const el = document.querySelector('.react-flow');
              const W = el?.clientWidth || window.innerWidth;
              const H = (el?.clientHeight || window.innerHeight) - lift;
              const zoom = Math.max(0.3, Math.min(1.1, W / (w * 1.3), H / (h * 1.3)));
              setCenter(b.x + b.width / 2, b.y + b.height / 2 + (lift / 2) / zoom,
                { zoom, duration: simple ? 0 : 320 });
            } else {
              fitBounds({ x: b.x - (w - b.width) / 2, y: b.y - (h - b.height) / 2, width: w, height: h },
                { padding: 0.22, duration: simple ? 0 : 320 });
            }
          } catch { /* encuadre best-effort */ }
          await sleep(simple ? 0 : 380);
        }

        for (const id of order) {
          if (cancelled.current) return;
          const node = nodes.find((n) => n.id === id);
          if (!node) { revealNode(id); continue; }

          const toolEl = document.querySelector(`[data-testid="tool-${node.type}"]`);
          const visibleTool = toolEl && toolEl.getBoundingClientRect().height > 0;

          if (visibleTool && !simple) {
            // 1) Scroll suave del panel hasta la herramienta.
            const cont = document.querySelector('.tool-palette-content');
            if (cont) {
              const cr = cont.getBoundingClientRect();
              const tr = toolEl.getBoundingClientRect();
              const delta = (tr.top - cr.top) - (cr.height / 2) + (tr.height / 2);
              if (Math.abs(delta) > 8) {
                cont.scrollTo({ top: cont.scrollTop + delta, behavior: 'smooth' });
                await sleep(360 * f);
              }
            }
            if (cancelled.current) return;

            // 2) Resaltar la herramienta + cursor virtual.
            const rect = toolEl.getBoundingClientRect();
            setHalo({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
            setCursor({ x: rect.left + rect.width * 0.62, y: rect.top + rect.height * 0.62 });
            await sleep(260 * f);
            if (cancelled.current) return;

            // 3) Clon visual viajando del panel al canvas.
            const to = flowToScreenPosition({ x: node.position.x, y: node.position.y });
            const dur = Math.round(560 * f);
            setGhost({
              x: rect.left, y: rect.top, w: rect.width, h: rect.height,
              dx: 0, dy: 0, dur, html: toolEl.innerHTML,
            });
            await sleep(30);
            const dx = to.x - rect.left;
            const dy = to.y - rect.top;
            setGhost((g) => (g ? { ...g, dx, dy } : g));
            setCursor({ x: to.x + 26, y: to.y + 26 });
            await sleep(dur + 60);
            if (cancelled.current) return;

            setGhost(null);
            setHalo(null);
          } else if (!simple) {
            // Sin herramienta visible (panel colapsado, filtrado, mobile):
            // no bloquear, el nodo entra con fade directo.
            await sleep(120 * f);
          }

          // 4) Nodo real visible + su conexión.
          revealNode(id);
          await sleep(simple ? 70 : 160 * f);
          if (revealReadyEdges() && !simple) await sleep(200 * f);
        }

        revealReadyEdges();
      } finally {
        setGhost(null);
        setHalo(null);
        setCursor(null);
        if (!cancelled.current) {
          // 'targets': el realce a "Ejecutar flujo completo" sólo si se
          // reveló un flujo de verdad (3 nodos o más); 'all' queda igual.
          finish({ spotlight: frame === 'targets' ? order.length >= 3 : true });
        }
        runningRef.current = false;
      }
    };

    run();

    // Cualquier interacción real con el canvas manda: se salta la animación.
    const canvas = document.querySelector('.canvas-container');
    const onInteract = () => skip();
    canvas?.addEventListener('pointerdown', onInteract);
    canvas?.addEventListener('wheel', onInteract, { passive: true });

    return () => {
      cancelled.current = true;
      runningRef.current = false;
      canvas?.removeEventListener('pointerdown', onInteract);
      canvas?.removeEventListener('wheel', onInteract);
    };
  }, [active, reduced, revealNode, revealEdge, finish, skip, flowToScreenPosition, fitBounds, setCenter]);

  // ── Guía visual hacia "Ejecutar flujo completo" ───────
  useEffect(() => {
    if (!spotlightRun) { setRunArrow(null); return undefined; }
    const btn = document.querySelector('[data-testid="run-workflow-btn"]');
    // Envío LMX-4 (I-31): el botón se oculta si el flujo todavía no se puede
    // ejecutar (faltan archivos de entrada): entonces no hay flecha.
    if (btn && !btn.offsetParent) { clearSpotlight(); return undefined; }
    if (btn) {
      const r = btn.getBoundingClientRect();
      setRunArrow({ x: r.left + r.width / 2, y: r.bottom + 10 });
    }
    const t = setTimeout(() => clearSpotlight(), 9000);
    return () => clearTimeout(t);
  }, [spotlightRun, clearSpotlight]);

  if (!active && !spotlightRun) return null;

  return createPortal(
    <div className="reveal-layer" data-testid="workflow-reveal-overlay">
      {halo && (
        <div
          className="reveal-halo"
          style={{ left: halo.x, top: halo.y, width: halo.w, height: halo.h }}
        />
      )}

      {ghost && (
        <div
          className="reveal-ghost"
          data-testid="reveal-ghost"
          style={{
            left: ghost.x,
            top: ghost.y,
            width: ghost.w,
            minHeight: ghost.h,
            transform: `translate3d(${ghost.dx}px, ${ghost.dy}px, 0)`,
            transitionDuration: `${ghost.dur}ms`,
          }}
          // Clon visual del item real del panel: no dispara el drag del editor.
          dangerouslySetInnerHTML={{ __html: ghost.html }}
        />
      )}

      {cursor && (
        <div className="reveal-cursor" style={{ left: cursor.x, top: cursor.y }}>
          <MousePointer2 size={20} />
        </div>
      )}

      {active && (
        <button
          type="button"
          className="reveal-skip"
          onClick={skip}
          data-testid="reveal-skip"
        >
          <SkipForward size={13} /> Saltar animación
        </button>
      )}

      {spotlightRun && (
        <>
          <div className="reveal-done" data-testid="reveal-done-banner">
            <Sparkles size={15} />
            <span>
              ¡Ya está todo listo! Presioná <b>“Ejecutar flujo completo”</b> para
              iniciar tu creación.
            </span>
          </div>
          {runArrow && (
            <div
              className="reveal-run-arrow"
              style={{ left: runArrow.x, top: runArrow.y }}
              data-testid="reveal-run-arrow"
            >
              <ArrowUp size={18} />
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
};

export default WorkflowRevealOverlay;
